import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '../Common/Drawer';
import { getTeam, getCandidates, addMember, removeMember } from '../../api/projectTeamApi';
import './ManageTeamDrawer.css';

// NF-S1: PM-facing drawer for managing a project's team (consultants only).
// Each operation is its own request (no batch save) per spec §B item 8.
const ManageTeamDrawer = ({ projectId, projectName, open, onClose }) => {
  const [members, setMembers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showFormer, setShowFormer] = useState(false);   // CEV-S3
  // CEV-S3 — people who have left stay in the team as history, in their own collapsed group.
  const currentMembers = useMemo(() => members.filter((m) => !m.isFormer), [members]);
  const formerMembers = useMemo(() => members.filter((m) => m.isFormer), [members]);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [m, c] = await Promise.all([getTeam(projectId), getCandidates(projectId)]);
      setMembers(m || []);
      setCandidates(c || []);
      setSelectedCandidate(null);
    } catch (e) {
      const data = e?.response?.data;
      setError((data && typeof data === 'object' && data.message) || (typeof data === 'string' ? data : null) || e?.message || 'Failed to load team.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const filteredCandidates = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return candidates;
    return candidates.filter((c) =>
      String(c.fullName || '').toLowerCase().includes(s)
      || String(c.email || '').toLowerCase().includes(s));
  }, [candidates, search]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
  };

  const onAdd = async () => {
    if (!selectedCandidate) return;
    setBusy(true);
    setError(null);
    try {
      await addMember(projectId, selectedCandidate.employeeId);
      await refresh();
      setSearch('');
    } catch (e) {
      const data = e?.response?.data;
      setError((data && typeof data === 'object' && data.message) || (typeof data === 'string' ? data : null) || 'Add failed.');
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (member) => {
    setBusy(true);
    setError(null);
    try {
      await removeMember(projectId, member.id);
      await refresh();
    } catch (e) {
      const data = e?.response?.data;
      setError((data && typeof data === 'object' && data.message) || (typeof data === 'string' ? data : null) || 'Remove failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const footer = (
    <button type="button" className="ppd-btn" onClick={onClose} disabled={busy} data-testid="mt-close-btn">Close</button>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="md"
      title={`Manage Team — ${projectName || ''}`}
      footer={footer}
      testId="mt-drawer"
    >
      {error && <div className="mt-error" role="alert" data-testid="mt-error">{error}</div>}

      <div className="mt-section">
        <div className="mt-section-title">Add consultant</div>
        <div className="mt-add-row">
          <input
            type="text"
            className="mt-search"
            value={search}
            placeholder="Search by name or email"
            onChange={(e) => setSearch(e.target.value)}
            data-testid="mt-search"
          />
          <select
            className="mt-candidate-select"
            value={selectedCandidate ? String(selectedCandidate.employeeId) : ''}
            onChange={(e) => {
              const id = parseInt(e.target.value, 10);
              setSelectedCandidate(filteredCandidates.find((c) => c.employeeId === id) || null);
            }}
            data-testid="mt-candidate-select"
          >
            <option value="">— Select consultant —</option>
            {filteredCandidates.map((c) => (
              <option key={c.employeeId} value={c.employeeId}>
                {c.fullName} ({c.email})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ppd-btn pri"
            onClick={onAdd}
            disabled={!selectedCandidate || busy}
            data-testid="mt-add-btn"
          >
            Add
          </button>
        </div>
      </div>

      <div className="mt-section">
        <div className="mt-section-title">Current members ({currentMembers.length})</div>
        {loading ? (
          <div className="mt-empty">Loading…</div>
        ) : currentMembers.length === 0 ? (
          <div className="mt-empty">No team members yet.</div>
        ) : (
          <ul className="mt-list" data-testid="mt-list">
            {currentMembers.map((m) => (
              <li key={m.id} className="mt-row" data-testid={`mt-row-${m.id}`}>
                <div className="mt-row-text">
                  <div className="mt-name">{m.fullName}</div>
                  <div className="mt-meta">{m.email} · added {fmtDate(m.addedAt)}</div>
                </div>
                <button
                  type="button"
                  className="mt-remove"
                  title="Remove from team"
                  onClick={() => onRemove(m)}
                  disabled={busy}
                  data-testid={`mt-remove-${m.id}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* CEV-S3 — former members: history only, not offered for work, not removable */}
      {!loading && formerMembers.length > 0 && (
        <div className="mt-section">
          <button type="button" className="mt-section-title" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            onClick={() => setShowFormer((v) => !v)} data-testid="mt-former-toggle">
            {showFormer ? '▾' : '▸'} Former members ({formerMembers.length})
          </button>
          {showFormer && (
            <ul className="mt-list" data-testid="mt-former-list">
              {formerMembers.map((m) => (
                <li key={m.id} className="mt-row" data-testid={`mt-former-${m.id}`} style={{ opacity: 0.7 }}>
                  <div className="mt-row-text">
                    <div className="mt-name">{m.fullName}</div>
                    <div className="mt-meta">Left {fmtDate(m.leftOn)} · {Number(m.loggedHours || 0).toLocaleString()} h logged</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ManageTeamDrawer;
